/**
 * OSD Context
 *
 * Provides global OSD state management with URL synchronization
 * Manages OSD selection and navigation with /:osd/* URL pattern
 *
 * Features:
 * - Extracts OSD from URL params (/:osd/*)
 * - Syncs OSD changes with URL navigation
 * - Provides navigation helpers with automatic OSD prefix
 * - Validates OSD codes
 * - Defaults to TAURON when no/invalid OSD
 */

/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import type { OSDContextValue, OSDProviderProps } from './OSDContext.types';
import type { OSDCode } from '../utils/osdMetadata';
import { isValidOSDCode, getDefaultOSD } from '../utils/osdMetadata';

/**
 * OSD Context
 */
const OSDContext = createContext<OSDContextValue | undefined>(undefined);

/**
 * OSD Provider Component
 *
 * Wraps the application and provides OSD state with URL synchronization
 */
export function OSDProvider({ children }: OSDProviderProps) {
  const params = useParams<{ osd?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract OSD from URL params, validate, or use default
  const getOSDFromURL = useCallback((): OSDCode => {
    const urlOSD = params.osd;

    if (urlOSD && isValidOSDCode(urlOSD)) {
      return urlOSD;
    }

    return getDefaultOSD();
  }, [params.osd]);

  const [currentOSD, setCurrentOSDState] = useState<OSDCode | null>(getOSDFromURL());

  // Sync with URL changes (browser back/forward)
  useEffect(() => {
    const osdFromURL = getOSDFromURL();
    setCurrentOSDState(osdFromURL);
  }, [getOSDFromURL]);

  /**
   * Check if a string is a valid OSD code
   */
  const isValidOSD = useCallback((osd: string): boolean => {
    return isValidOSDCode(osd);
  }, []);

  /**
   * Set the current OSD
   * Updates state and navigates to new OSD URL
   */
  const setOSD = useCallback(
    (osd: OSDCode) => {
      setCurrentOSDState(osd);

      // Get current path without OSD prefix
      const pathParts = location.pathname.split('/').filter(Boolean);
      const currentOSDInPath = pathParts[0];

      let newPath: string;

      if (currentOSDInPath && isValidOSDCode(currentOSDInPath)) {
        // Replace existing OSD in URL
        pathParts[0] = osd;
        newPath = `/${pathParts.join('/')}`;
      } else {
        // Prepend OSD to current path
        newPath = `/${osd}${location.pathname}`;
      }

      navigate(newPath, { replace: true });
    },
    [location.pathname, navigate]
  );

  /**
   * Get a path with current OSD prefix
   * Does not navigate, just returns the prefixed path
   */
  const getOSDPath = useCallback(
    (path: string): string => {
      const osd = currentOSD || getDefaultOSD();
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      return `/${osd}${normalizedPath}`;
    },
    [currentOSD]
  );

  /**
   * Navigate to a path with current OSD prefix
   */
  const navigateWithOSD = useCallback(
    (path: string) => {
      const osdPath = getOSDPath(path);
      navigate(osdPath);
    },
    [getOSDPath, navigate]
  );

  const value: OSDContextValue = {
    currentOSD,
    setOSD,
    isValidOSD,
    navigateWithOSD,
    getOSDPath,
  };

  return <OSDContext.Provider value={value}>{children}</OSDContext.Provider>;
}

/**
 * Default context value when used outside OSDProvider
 * Provides safe fallback functions that work without provider
 */
const defaultContextValue: OSDContextValue = {
  currentOSD: null,
  setOSD: () => {
    console.warn('useOSD: setOSD called outside OSDProvider - no effect');
  },
  isValidOSD: (osd: string) => {
    return isValidOSDCode(osd);
  },
  navigateWithOSD: () => {
    console.warn('useOSD: navigateWithOSD called outside OSDProvider - no effect');
  },
  getOSDPath: (path: string) => {
    // Return path without OSD prefix when outside provider
    return path.startsWith('/') ? path : `/${path}`;
  },
};

/**
 * Hook to use OSD context
 *
 * Returns default/fallback values when used outside of OSDProvider.
 * This allows components like Header to work on pages without OSD context.
 */
export function useOSD(): OSDContextValue {
  const context = useContext(OSDContext);

  // Return default context when used outside OSDProvider
  // This allows Header and other shared components to work on non-OSD pages
  if (context === undefined) {
    return defaultContextValue;
  }

  return context;
}
